function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * Numeric "compare" cabinet filters (Exactly / Greater than / Less than / Between /
 * Empty value).
 *
 * A filter is compare-capable when its cabinet filter definition declares
 * `type: 'numericRange'`. Selections live in `indexQuery.filters.compare` keyed by index:
 *
 *   filters.compare = { weight: { operator: 'between', value: '10000', value2: '30000' } }
 *
 * `compare` is a plain object, so `optimizeIndexQueryFilters` (which only rewrites arrays)
 * passes it through untouched. Saved views do not persist `compare` as-is: they flatten it
 * into the shared keyed `numericRange` shape via `serializeCabinetFiltersForSavedView`.
 *
 * The request param is derived from that state, never stored alongside it, so there is a
 * single source of truth. The server contract (`greaterThanLessThan`) understands
 * `greaterThan` / `lessThan` / `isEmpty`; every UI operator maps onto those.
 */

export const COMPARE_FILTERS_KEY = 'compare';
export const NUMERIC_RANGE_FILTER_TYPE = 'numericRange';
export const DEFAULT_COMPARE_OPERATOR = 'exactly';
export const EMPTY_COMPARE_OPERATOR = 'empty';
const greaterThanBound = (index, value, inclusive) => ({
  direction: 'greaterThan',
  index,
  value,
  inclusive
});
const lessThanBound = (index, value, inclusive) => ({
  direction: 'lessThan',
  index,
  value,
  inclusive
});

/**
 * Everything each operator needs, in one place: the dropdown `label`, how many value inputs
 * it shows (`inputCount`), the `symbol` for the filter rail, and the `bounds` the request
 * carries. Adding an operator means adding one entry here.
 *
 * Bounds expand onto the directions the server already understands. "Exactly" is
 * `>= v AND <= v`, which avoids adding a new direction to the server contract.
 * "Empty value" uses the dedicated `isEmpty` direction (no bound value).
 */
export const COMPARE_OPERATORS = [{
  id: DEFAULT_COMPARE_OPERATOR,
  label: 'Exactly',
  inputCount: 1,
  symbol: '=',
  bounds: (index, value) => [greaterThanBound(index, value, true), lessThanBound(index, value, true)]
}, {
  id: 'greaterThan',
  label: 'Greater than',
  inputCount: 1,
  symbol: '>',
  bounds: (index, value) => [greaterThanBound(index, value, false)]
}, {
  id: 'greaterThanOrEqual',
  label: 'Greater than or equal',
  inputCount: 1,
  symbol: '>=',
  bounds: (index, value) => [greaterThanBound(index, value, true)]
}, {
  id: 'lessThan',
  label: 'Less than',
  inputCount: 1,
  symbol: '<',
  bounds: (index, value) => [lessThanBound(index, value, false)]
}, {
  id: 'lessThanOrEqual',
  label: 'Less than or equal',
  inputCount: 1,
  symbol: '<=',
  bounds: (index, value) => [lessThanBound(index, value, true)]
}, {
  id: 'between',
  label: 'Between ... and ...',
  inputCount: 2,
  bounds: (index, value, secondValue) => [greaterThanBound(index, value, true), lessThanBound(index, secondValue, true)]
}, {
  id: EMPTY_COMPARE_OPERATOR,
  label: 'Empty value',
  inputCount: 0,
  bounds: index => [{
    direction: 'isEmpty',
    index
  }]
}];
const findCompareOperator = operatorId => COMPARE_OPERATORS.find(({
  id
}) => id === operatorId);

/**
 * Parse a user-entered value into a finite number. Thousands separators are accepted
 * because the values these filters target are commonly typed as `10,000`.
 *
 * @param {unknown} value
 * @returns {number|null} null when the input is empty or not a number
 */
export const toComparableNumber = value => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const normalized = String(value).replace(/,/g, '').trim();
  if (!normalized) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * @param {string} operatorId
 * @returns {{ id: string, label: string, inputCount: number }} the default when unknown
 */
export const getCompareOperator = operatorId => findCompareOperator(operatorId) || findCompareOperator(DEFAULT_COMPARE_OPERATOR);

/**
 * True when the cabinet filter definition opts into numeric comparison. The server reads
 * the same `type` off the filter definition to decide whether to cast, so the control that
 * is shown and the comparison that runs stay in step.
 *
 * @param {{ type?: string }} [filterDefinition]
 * @returns {boolean}
 */
export const isNumericRangeFilter = filterDefinition => (filterDefinition === null || filterDefinition === void 0 ? void 0 : filterDefinition.type) === NUMERIC_RANGE_FILTER_TYPE;

/**
 * True when Between has both ends filled and the minimum is greater than the maximum.
 * Those selections are kept in UI state so the user can fix them, but they must not
 * become request bounds (that would match nothing with no explanation).
 *
 * @param {{ operator?: string, value?: unknown, value2?: unknown }} [entry]
 * @returns {boolean}
 */
export const hasInvertedBetweenBounds = entry => {
  if (!entry || entry.operator !== 'between') {
    return false;
  }
  const first = toComparableNumber(entry.value);
  const second = toComparableNumber(entry.value2);
  return first !== null && second !== null && first > second;
};

/**
 * Bounds for one index. Incomplete input (e.g. "Between" with only a minimum) yields
 * just the bounds that are filled in, so the list narrows as the user types.
 *
 * @param {string} index
 * @param {{ operator?: string, value?: unknown, value2?: unknown }} [entry]
 * @returns {Array<object>}
 */
const buildIndexBounds = (index, entry) => {
  const {
    operator = DEFAULT_COMPARE_OPERATOR
  } = entry || {};
  // Unknown operators produce no bounds rather than silently falling back to the default.
  const definition = findCompareOperator(operator);
  if (!definition) {
    return [];
  }
  if (hasInvertedBetweenBounds(entry)) {
    return [];
  }
  const bounds = definition.bounds(index, toComparableNumber(entry === null || entry === void 0 ? void 0 : entry.value), toComparableNumber(entry === null || entry === void 0 ? void 0 : entry.value2));
  return bounds.filter(bound => bound.direction === 'isEmpty' || bound.value !== null);
};

/**
 * @param {Record<string, object>} [compare]
 * @returns {Array<object>} the `greaterThanLessThan` request param
 */
export const buildGreaterThanLessThan = compare => {
  if (!compare || typeof compare !== 'object' || Array.isArray(compare)) {
    return [];
  }
  return Object.entries(compare).flatMap(([index, entry]) => buildIndexBounds(index, entry));
};

/**
 * Derive the `greaterThanLessThan` request param from `query.filters.compare`. Called at
 * each cabinet entry point so paging, sorting, saved views, and the row count all agree.
 *
 * @param {Record<string, unknown>} query
 * @returns {Record<string, unknown>}
 */
export const withCompareRangeParams = query => {
  var _query$filters;
  if (!query || typeof query !== 'object') {
    return query;
  }
  const greaterThanLessThan = buildGreaterThanLessThan((_query$filters = query.filters) === null || _query$filters === void 0 ? void 0 : _query$filters[COMPARE_FILTERS_KEY]);
  const next = _objectSpread({}, query);
  if (greaterThanLessThan.length) {
    next.greaterThanLessThan = greaterThanLessThan;
  } else {
    // Clearing the filter must also clear a param left over from the previous request.
    delete next.greaterThanLessThan;
  }
  return next;
};

/**
 * @param {Record<string, unknown>} [filters]
 * @param {string} index
 * @returns {{ operator?: string, value?: unknown, value2?: unknown }|null}
 */
export const getCompareEntry = (filters, index) => {
  var _filters$COMPARE_FILT;
  return (filters === null || filters === void 0 || (_filters$COMPARE_FILT = filters[COMPARE_FILTERS_KEY]) === null || _filters$COMPARE_FILT === void 0 ? void 0 : _filters$COMPARE_FILT[index]) || null;
};

/**
 * Set or remove one index's compare selection. An entry with no values is kept so the
 * operator the user picked survives re-renders; it simply produces no bounds.
 *
 * @param {Record<string, unknown>} [filters]
 * @param {string} index
 * @param {object|null} entry pass null to remove
 * @returns {Record<string, unknown>}
 */
export const withCompareEntry = (filters, index, entry) => {
  const existingCompare = (filters || {})[COMPARE_FILTERS_KEY];
  const compare = _objectSpread({}, existingCompare && typeof existingCompare === 'object' && !Array.isArray(existingCompare) ? existingCompare : {});
  if (entry) {
    compare[index] = entry;
  } else {
    delete compare[index];
  }
  const next = _objectSpread({}, filters || {});
  if (Object.keys(compare).length) {
    next[COMPARE_FILTERS_KEY] = compare;
  } else {
    delete next[COMPARE_FILTERS_KEY];
  }
  return next;
};

/**
 * @param {Record<string, unknown>} [filters]
 * @returns {Record<string, unknown>} filters without any compare selections
 */
export const withoutCompareFilters = filters => {
  const next = _objectSpread({}, filters || {});
  delete next[COMPARE_FILTERS_KEY];
  return next;
};
const formatCompareValue = value => {
  const parsed = toComparableNumber(value);
  return parsed === null ? '' : parsed.toLocaleString('en-US');
};

/**
 * Short label for the filter rail, e.g. `>= 10,000` or `10,000 - 30,000`.
 *
 * @param {{ operator?: string, value?: unknown, value2?: unknown }} [entry]
 * @returns {string} empty when nothing is set
 */
export const compareSummary = entry => {
  const {
    operator = DEFAULT_COMPARE_OPERATOR
  } = entry || {};
  const {
    inputCount,
    label,
    symbol
  } = getCompareOperator(operator);
  // A value-less operator is fully described by its label.
  if (inputCount === 0) {
    return label;
  }
  if (hasInvertedBetweenBounds(entry)) {
    return '';
  }
  const first = formatCompareValue(entry === null || entry === void 0 ? void 0 : entry.value);
  if (inputCount > 1) {
    const second = formatCompareValue(entry === null || entry === void 0 ? void 0 : entry.value2);
    if (!first && !second) {
      return '';
    }
    return `${first || '...'} - ${second || '...'}`;
  }
  if (!first) {
    return '';
  }
  return `${symbol || ''} ${first}`.trim();
};
export default withCompareRangeParams;